import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GitBranch, Filter, HelpCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Panel, Skeleton, PIPELINE_SHADES } from "./primitives";

// Connected, graduated blue->teal stage progression with drill-down.
export function PipelineFunnel({ data, loading, pipeline, setPipeline, pipelines, drill, index }) {
  const reduce = useReducedMotion();
  const stages = data?.pipeline?.stages || [];
  const total = data?.pipeline?.total || 0;
  const showPct = data?.pipeline?.meta?.mode === "all" && total > 0;
  return (
    <Panel title="Recruiting Pipeline" icon={GitBranch} testId="pipeline-funnel" index={index}
      action={<div className="flex items-center gap-2">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild><button className="text-slate-300 hover:text-slate-500" data-testid="pipeline-help"><HelpCircle className="w-3.5 h-3.5" /></button></TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs">Percentage of candidates currently represented in this pipeline snapshot — not a historical conversion rate.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Select value={pipeline} onValueChange={setPipeline}>
          <SelectTrigger className="h-8 w-52 text-xs" data-testid="pipeline-funnel-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">All Pipelines</SelectItem>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>}>
      {loading && !data ? <Skeleton h="h-28" /> : (
        <div className="flex items-stretch gap-1.5 overflow-x-auto tr-scroll pb-1" data-testid="pipeline-stages">
          {stages.map((s, i) => {
            const pct = total ? Math.round((s.count / total) * 100) : 0;
            const shade = PIPELINE_SHADES[i] || PIPELINE_SHADES[PIPELINE_SHADES.length - 1];
            const nav = s.stage_id ? { tab: "all", pipeline_id: s.pipeline_id, stage_id: s.stage_id } : { tab: "all" };
            return (
              <motion.button key={s.key} data-testid={`funnel-stage-${i}`} onClick={() => drill(nav)}
                initial={reduce ? false : { opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.04, ease: "easeOut" }}
                style={{ background: shade }}
                className="group relative flex-1 min-w-[96px] rounded-lg px-2.5 py-3.5 text-center text-white shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
                <p className="text-2xl font-bold leading-none">{s.count}</p>
                <p className="text-[11px] font-medium leading-tight mt-1 opacity-90 truncate">{s.name}</p>
                {showPct && <p className="text-[10px] mt-1 opacity-70">{pct}%</p>}
              </motion.button>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

// YTD cohort funnel (distinct from the live pipeline snapshot): descending centered bars.
export function ConversionFunnel({ data, loading, drill, index }) {
  const reduce = useReducedMotion();
  const rows = data?.conversion || [];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Panel title="YTD Conversion Funnel" icon={Filter} testId="conversion-funnel" index={index}>
      {loading && !data ? <Skeleton h="h-40" /> : (
        <div className="space-y-2.5" data-testid="conversion-rows">
          {rows.map((s, i) => {
            const w = Math.round((s.count / max) * 100);
            const prev = rows[i - 1];
            const pct = i > 0 && prev && prev.count > 0 ? Math.round((s.count / prev.count) * 100) : null;
            return (
              <div key={s.key} data-testid={`conversion-${s.key}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-slate-600">{s.label}</span>
                  <span className="text-slate-400">{s.count}{pct != null ? ` · ${pct}% of prev` : ""}</span>
                </div>
                <div className="h-7 rounded-md bg-slate-100 overflow-hidden">
                  <motion.div className="h-full rounded-md flex items-center justify-end pr-2"
                    style={{ background: PIPELINE_SHADES[Math.min(i * 3, PIPELINE_SHADES.length - 1)] }}
                    initial={reduce ? false : { width: 0 }} animate={{ width: `${Math.max(w, s.count > 0 ? 8 : 0)}%` }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}>
                    {s.count > 0 && <span className="text-[11px] font-semibold text-white">{s.count}</span>}
                  </motion.div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
